/**
 * Layout primitives for the /account dashboard: the card, page header, stat
 * tile and empty state the account and workspace routes share.
 *
 * Status pills live in ./status.tsx, which imports Card from here.
 */

import { type ReactNode, useState } from "react";
import {
  HiChevronDoubleLeft,
  HiChevronDoubleRight,
  HiChevronLeft,
  HiChevronRight,
} from "react-icons/hi2";
import type { IconProps } from "../icons/index.tsx";
import { PAGE_SIZES, pageItems, parsePageSize } from "../lib/legacy.ts";

/** Shared input styling for the account forms. */
export const FIELD =
  "block w-full rounded-lg border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500";

/** Page-header buttons, one height across the dashboard. */
export const BTN =
  "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50";
export const OUTLINE_BTN = `${BTN} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;
export const PRIMARY_BTN = `${BTN} bg-primary-600 text-white shadow-sm hover:bg-primary-700`;

interface CardProps {
  title?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** Override to drop padding when the body is a full-bleed table or list. */
  bodyClass?: string;
  children: ReactNode;
}

/**
 * Caps a list at the space under the sticky header and the shell's py-6.
 * The card stays as tall as its rows. Past that cap, only ROW_SCROLL scrolls,
 * so the pagination bar stays against the last row.
 */
export const FILL_PAGE = "flex max-h-[calc(100dvh-var(--header-h)-3rem)] min-h-0 flex-col";
export const FILL_CARD = "flex min-h-0 min-w-0 flex-col";
export const FILL_BODY = "flex min-h-0 flex-col overflow-hidden p-0";
export const ROW_SCROLL = "min-h-0 overflow-auto";
/** Column titles stay put while the row area scrolls. */
export const STICKY_THEAD =
  "sticky top-0 z-10 border-b border-gray-100 bg-white text-xs font-semibold uppercase tracking-wide text-gray-500";

export function Card({ title, action, className = "", bodyClass = "p-5", children }: CardProps) {
  return (
    <section className={`rounded-2xl border border-gray-200 bg-white shadow-sm ${className}`}>
      {(title || action) && (
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
          {title && <h2 className="text-base font-semibold text-gray-900">{title}</h2>}
          {action}
        </div>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-6 flex shrink-0 flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

/** Uppercase label with a hint that stays hidden until the info icon is opened. */
export function SectionLabel({
  label,
  hint,
  open,
  onToggle,
  heading = false,
  children,
}: {
  label: string;
  hint: string;
  open: boolean;
  onToggle(): void;
  heading?: boolean;
  /** Replaces the uppercase label. The info icon stays. */
  children?: ReactNode;
}) {
  const Tag = heading ? "h3" : "p";
  return (
    <>
      <div className="flex min-w-0 items-center gap-1.5">
        {children ?? (
          <Tag className="m-0 text-xs font-semibold uppercase leading-4 tracking-wide text-gray-500">
            {label}
          </Tag>
        )}
        <button
          type="button"
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label={hint}
          aria-expanded={open}
          onClick={onToggle}
        >
          <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden="true">
            <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <path
              d="M8 7.2v3.8"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <circle cx="8" cy="4.8" r="0.75" fill="currentColor" />
          </svg>
        </button>
      </div>
      {open && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </>
  );
}

export function StatCard({
  label,
  value,
  hint,
  Icon,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  Icon: (p: IconProps) => ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
        <span className="rounded-lg bg-primary-50 p-2 text-primary-600">
          <Icon className="h-5 w-5" />
        </span>
      </div>
      <p className="mt-3 text-3xl font-semibold tabular-nums tracking-tight text-gray-900">
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

/** Labelled row of filter controls, as on the storefront. */
export function FilterGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

export function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={`rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
        active
          ? "border-primary-600 bg-primary-50 text-primary-700"
          : "border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-900"
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

const PAGE_SIZE_KEY = "econnect.page-size";
const PAGE_BTN =
  "flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-sm font-medium tabular-nums transition-colors";

/** The row count shared by every paged list, remembered until it is changed. */
export function usePageSize(): [number, (size: number) => void] {
  const [size, setSize] = useState(() => {
    try {
      return parsePageSize(localStorage.getItem(PAGE_SIZE_KEY));
    } catch {
      return PAGE_SIZES[0];
    }
  });
  const set = (n: number) => {
    const next = parsePageSize(String(n));
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(next));
    } catch {
      // Storage full or blocked: this list still pages, the next visit uses the default.
    }
    setSize(next);
  };
  return [size, set];
}

/**
 * Count, rows menu, and page buttons. Renders once there are more rows than
 * the smallest page size, so a larger size can still be shortened. `page` is
 * 0-based. With four or fewer pages only the numbers show. Past that,
 * buttons that cannot move are left out.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPage,
  onPageSize,
  single = false,
  label,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize?: (size: number) => void;
  /** One item per page, no rows menu. Shown whenever there is more than one item. */
  single?: boolean;
  label?: string;
}) {
  const size = single ? 1 : pageSize;
  if (single ? total <= 1 : total <= PAGE_SIZES[0]) return null;
  const pageCount = Math.ceil(total / size);
  const jumps = pageCount > 4;
  const step = (to: number, label: string, Icon: (p: { className?: string }) => ReactNode) => {
    if (to < 0 || to >= pageCount || to === page) return null;
    return (
      <button
        type="button"
        aria-label={label}
        title={label}
        className={`${PAGE_BTN} text-gray-500 hover:bg-gray-100 hover:text-gray-900`}
        onClick={() => onPage(to)}
      >
        <Icon className="h-4 w-4" />
      </button>
    );
  };
  return (
    <div
      className={`flex shrink-0 flex-wrap items-center justify-between gap-3 text-sm text-gray-600 ${
        single ? "pb-2" : "border-t border-gray-100 px-5 py-3"
      }`}
    >
      <span className="tabular-nums">
        {single
          ? (label ?? `${page + 1} of ${total}`)
          : `Showing ${page * size + 1}–${Math.min(total, (page + 1) * size)} of ${total}`}
      </span>
      <div className="flex flex-wrap items-center gap-4">
        {!single && onPageSize && (
          <label className="flex items-center gap-2">
            Rows
            <select
              className="rounded-lg border-gray-300 py-1.5 pl-3 pr-8 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
              value={pageSize}
              onChange={(e) => onPageSize(Number(e.target.value))}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        <nav aria-label="Pagination" className="flex items-center gap-1">
          {jumps && step(0, "First page", HiChevronDoubleLeft)}
          {jumps && step(page - 1, "Previous page", HiChevronLeft)}
          {pageItems(page, pageCount).map((n, i) =>
            n === "…" ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: gaps have no identity of their own
              <span key={`gap-${i}`} className="px-1 text-sm text-gray-400">
                …
              </span>
            ) : (
              <button
                key={n}
                type="button"
                aria-current={n === page ? "page" : undefined}
                className={`${PAGE_BTN} ${
                  n === page
                    ? "bg-primary-600 text-white shadow-sm"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => onPage(n)}
              >
                {n + 1}
              </button>
            ),
          )}
          {jumps && step(page + 1, "Next page", HiChevronRight)}
          {jumps && step(pageCount - 1, "Last page", HiChevronDoubleRight)}
        </nav>
      </div>
    </div>
  );
}

export function EmptyState({
  Icon,
  title,
  message,
  action,
  className = "",
}: {
  Icon: (p: IconProps) => ReactNode;
  title: string;
  message?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center px-6 py-14 text-center ${className}`}
    >
      <span className="rounded-full bg-gray-100 p-3 text-gray-400">
        <Icon className="h-6 w-6" />
      </span>
      <p className="mt-4 text-sm font-semibold text-gray-900">{title}</p>
      {message && <p className="mt-1 text-sm text-gray-500">{message}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
